import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";

const initialState = {
  fetchLoading: false,
  isLoading: false,
  products: {
    data: [],
    meta: {
      total: 0,
      from: 0,
      to: 0,
      current_page: 0,
      last_page: 0,
      per_page: 0,
    },
  },
  singleProduct: null,
};

export const createProduct = createAsyncThunk(
  "createProduct/product",
  async ({ data, enqueueSnackbar, navigate, path }, thunkApi) => {
    try {
      const response = await axiosInstance.post("products", data);
      enqueueSnackbar("Product is added successfully");
      navigate(path);
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const fetchProducts = createAsyncThunk(
  "fetchProducts/product",
  async (
    { enqueueSnackbar, orderable, page = 1, limit = 10, category, isAdminOnly },
    thunkApi
  ) => {
    try {
      const response = await axiosInstance.get("products", {
        params: {
          ...(orderable !== null ? { is_orderable: orderable ? 1 : 0 } : {}),
          page,
          limit,
          ...(category !== null ? { category } : {}),
          ...(isAdminOnly !== undefined && isAdminOnly !== ""
            ? { is_admin_only: isAdminOnly }
            : {}),
        },
      });

      const apiData = response.data.data; // Laravel wraps in .data

      return {
        data: apiData.data, // array of products
        meta: {
          total: apiData.total,
          from: apiData.from,
          to: apiData.to,
          current_page: apiData.current_page,
          last_page: apiData.last_page,
          per_page: apiData.per_page,
        },
      };
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch products";
      if (enqueueSnackbar) {
        enqueueSnackbar(message, { variant: "error" });
      }
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const fetchSingleProduct = createAsyncThunk(
  "fetchSingleProduct/product",
  async ({ enqueueSnackbar, slug }, thunkApi) => {
    try {
      const response = await axiosInstance.get(`products/${slug}`);

      return response.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const updateProduct = createAsyncThunk(
  "updateProduct/product",
  async ({ slug, data, enqueueSnackbar, setOpenEdit }, thunkApi) => {
    try {
      const response = await axiosInstance.post(`products/${slug}`, data);
      return {
        data: response.data.data,
        enqueueSnackbar,
        setOpenEdit,
        slug,
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const deleteProduct = createAsyncThunk(
  "deleteProduct/product",
  async ({ id, enqueueSnackbar }, thunkApi) => {
    try {
      await axiosInstance.delete(`products/${id}`);
      return { id, enqueueSnackbar };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

// TODO: delte product gallery

export const deleteSingleGallery = createAsyncThunk(
  "deleteSingleGallery/product",
  async (
    { id, img_name, enqueueSnackbar, setGalleries, product_id, galleries },
    thunkApi
  ) => {
    try {
      await axiosInstance.delete(`delete-image/${id}`);
      return {
        setGalleries,
        img_name,
        id,
        enqueueSnackbar,
        product_id,
        galleries,
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

const productSlice = createSlice({
  name: "product",
  initialState,
  extraReducers: (builder) => {
    // TODO: create product
    builder.addCase(createProduct.pending, (state, _) => {
      state.isLoading = true;
    });

    builder.addCase(createProduct.fulfilled, (state) => {
      state.isLoading = false;
    });

    builder.addCase(createProduct.rejected, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar(
        JSON.stringify(action.payload.error.message || action.payload.error),
        {
          variant: "error",
        }
      );
    });

    // ==========================

    // TODO: fetch product
    builder.addCase(fetchProducts.pending, (state, _) => {
      state.fetchLoading = true;
    });

    builder.addCase(fetchProducts.fulfilled, (state, action) => {
      state.fetchLoading = false;
      state.products = action.payload;
    });

    builder.addCase(fetchProducts.rejected, (state, action) => {
      state.fetchLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================

    // TODO: fetch single product
    builder.addCase(fetchSingleProduct.pending, (state, _) => {
      state.fetchLoading = true;
    });

    builder.addCase(fetchSingleProduct.fulfilled, (state, action) => {
      state.fetchLoading = false;
      state.singleProduct = action.payload;
    });

    builder.addCase(fetchSingleProduct.rejected, (state, action) => {
      state.fetchLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================

    // TODO: update product
    builder.addCase(updateProduct.pending, (state, _) => {
      state.isLoading = true;
    });

    builder.addCase(updateProduct.fulfilled, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar("Product is updated successfully");
      state.products.data = state.products.data.map((product) =>
        product.slug === action.payload.slug ? action.payload.data : product
      );
      // action.payload.setOpenEdit(false);
    });

    builder.addCase(updateProduct.rejected, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================

    // TODO: delete product
    builder.addCase(deleteProduct.pending, (state, _) => {
      state.isLoading = true;
    });

    builder.addCase(deleteProduct.fulfilled, (state, action) => {
      state.isLoading = false;
      state.products.data = state.products.data.filter(
        (product) => product.id !== action.payload.id
      );
      action.payload.enqueueSnackbar("Product is deleted successfully");
    });

    builder.addCase(deleteProduct.rejected, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================

    // TODO: delete product gallery
    builder.addCase(deleteSingleGallery.pending, (state, _) => {
      state.isLoading = true;
    });

    builder.addCase(deleteSingleGallery.fulfilled, (state, action) => {
      state.isLoading = false;
      // console.log(action.payload.galleries, "galleries", action.payload.id);
      action.payload.setGalleries((prev) =>
        prev.filter((f, index) => {
          return f.id !== action.payload.id;
        })
      );

      state.products.data = state.products.data.map((product) =>
        product.id === action.payload.product_id
          ? {
              ...product,
              gallery: action.payload.galleries.filter(
                (f) => f.id !== action.payload.id
              ),
            }
          : product
      );

      action.payload.enqueueSnackbar(`Gallery image is deleted successfully`);
    });

    builder.addCase(deleteSingleGallery.rejected, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================
  },
});

export default productSlice.reducer;
